import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import AutoHeight from 'embla-carousel-auto-height';
import { Box } from 'rebass/styled-components';
import { NavBar } from './navBar';
import { NavBarLegacy } from './navBarLegacy';
import RestrictContent from '../restrictContent';
import {
  BUSINESS_TOGGLES,
  getBusinessToggle,
} from '../../../utils/toggleHelpers';

import withCtaAnalytics from '../ctaAnalyticsHoc';

const BoxWithCtaAnalytics = withCtaAnalytics(Box, {
  staticModuleType: 'carousel',
});

const defaultPreviousSlideText = 'Previous slide';
const defaultNextSlideText = 'Next slide';
const defaultSelectSlideText = 'View slide %s';
const defaultPlayText = 'Start autoplay for slides';
const defaultPauseText = 'Stop autoplay for slides';

const Carousel = ({
  children,
  entryTitle,
  viewDuration, // in seconds
  areAllSlidesSameHeight = false,
  previousSlideText = defaultPreviousSlideText,
  nextSlideText = defaultNextSlideText,
  selectSlideText = defaultSelectSlideText,
  playText = defaultPlayText,
  pauseText = defaultPauseText,
  showProgressBar,
  slideFixedHeight = 350,
  slideMobileFixedHeight = null,
  slideSpacing = 30,
  restrictCarouselContent = false,
  maxWidth = undefined,
  promoBannerNoBottomSpace = false,
}) => {
  const navInsideBanner = getBusinessToggle(
    BUSINESS_TOGGLES.BANNER_CAROUSEL_NAV_INSIDE,
  );
  const getEmblaDependencies = () =>
    areAllSlidesSameHeight ? [] : [AutoHeight()];

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, slidesToScroll: 1, align: 'start', containScroll: false },
    getEmblaDependencies(),
  );
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const intervalRef = useRef(null);

  const handleStart = () => {
    setIsRunning(true);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTime(0);
    // Due to delay in update the states we have to add some time before call the handleStart
    setTimeout(() => {
      handleStart();
    }, 10);
  };

  const handlePlayPause = () => {
    setIsRunning(!isRunning);
  };

  // This is related to the NavArrow
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [
    emblaApi,
  ]);

  // This is related to the NavArrow
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [
    emblaApi,
  ]);

  // This is related to the dots
  const scrollTo = useCallback(index => emblaApi && emblaApi.scrollTo(index), [
    emblaApi,
  ]);

  const onSelect = useCallback(eApi => {
    setSelectedIndex(eApi.selectedScrollSnap());
    setPrevBtnDisabled(!eApi.canScrollPrev());
    setNextBtnDisabled(!eApi.canScrollNext());
    handleReset();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  const childrenLength = useMemo(
    () => React.Children.toArray(children).length,
    [children],
  );

  useEffect(() => {
    if (isRunning && childrenLength > 1) {
      intervalRef.current = setInterval(() => {
        setTime(prevTime => {
          if (emblaApi && prevTime >= viewDuration * 1000) {
            clearInterval(intervalRef.current);
            setIsRunning(false);
            emblaApi.scrollNext();
            return prevTime;
          }
          return prevTime + 100;
        });
      }, 100);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, childrenLength]);

  if (childrenLength === 0) {
    return null;
  }

  return (
    <RestrictContent
      restrictContent={restrictCarouselContent}
      maxWidth={maxWidth}
    >
      <BoxWithCtaAnalytics
        entryTitle={entryTitle}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...(!navInsideBanner ? { gap: 'md' } : {}),
          ...(restrictCarouselContent
            ? {
                paddingTop: '40px',
                paddingBottom: promoBannerNoBottomSpace ? '0px' : '40px',
                paddingLeft: ['md', 0],
                paddingRight: ['md', 0],
              }
            : {
                paddingBottom: 'md',
              }),
        }}
      >
        {navInsideBanner ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              ref={emblaRef}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                height: areAllSlidesSameHeight
                  ? [
                      `${slideMobileFixedHeight || slideFixedHeight}px`,
                      `${slideFixedHeight}px`,
                    ]
                  : 'unset',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  transition: 'height 0.1s',
                  height: areAllSlidesSameHeight
                    ? [
                        `${slideMobileFixedHeight || slideFixedHeight}px`,
                        `${slideFixedHeight}px`,
                      ]
                    : 'unset',
                }}
              >
                {React.Children.toArray(children).map((child, index) => (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${child?.props?.entryTitle}-${index}`}
                    sx={{
                      flex: `0 0 calc(100% + ${slideSpacing}px)`,
                      minWidth: 0,
                      paddingRight: `${slideSpacing}px`,
                      paddingBottom: childrenLength > 1 ? '60px' : undefined,
                      height: areAllSlidesSameHeight
                        ? [
                            `${slideMobileFixedHeight || slideFixedHeight}px`,
                            `${slideFixedHeight}px`,
                          ]
                        : 'unset',
                    }}
                  >
                    {child}
                  </Box>
                ))}
              </Box>
            </Box>
            {childrenLength > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '16px',
                  right: ['auto', '24px'],
                  left: ['50%', 'auto'],
                  transform: ['translateX(-50%)', 'none'],
                  zIndex: 9,
                }}
              >
                <NavBar
                  scrollTo={scrollTo}
                  handlePlayPause={handlePlayPause}
                  isRunning={isRunning}
                  playText={playText}
                  pauseText={pauseText}
                  selectSlideText={selectSlideText}
                  selectedIndex={selectedIndex}
                  viewDuration={viewDuration}
                  time={time}
                  showProgressBar={showProgressBar}
                >
                  {children}
                </NavBar>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Box
              ref={emblaRef}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                height: areAllSlidesSameHeight
                  ? [
                      `${slideMobileFixedHeight || slideFixedHeight}px`,
                      `${slideFixedHeight}px`,
                    ]
                  : 'unset',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  transition: 'height 0.1s',
                  height: areAllSlidesSameHeight
                    ? [
                        `${slideMobileFixedHeight || slideFixedHeight}px`,
                        `${slideFixedHeight}px`,
                      ]
                    : 'unset',
                }}
              >
                {React.Children.toArray(children).map((child, index) => (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${child?.props?.entryTitle}-${index}`}
                    sx={{
                      flex: `0 0 calc(100% + ${slideSpacing}px)`,
                      minWidth: 0,
                      paddingRight: `${slideSpacing}px`,
                      height: areAllSlidesSameHeight
                        ? [
                            `${slideMobileFixedHeight || slideFixedHeight}px`,
                            `${slideFixedHeight}px`,
                          ]
                        : 'unset',
                    }}
                  >
                    {child}
                  </Box>
                ))}
              </Box>
            </Box>
            <NavBarLegacy
              scrollTo={scrollTo}
              handlePlayPause={handlePlayPause}
              isRunning={isRunning}
              playText={playText}
              pauseText={pauseText}
              selectSlideText={selectSlideText}
              selectedIndex={selectedIndex}
              viewDuration={viewDuration}
              time={time}
              showProgressBar={showProgressBar}
              onPrevClick={scrollPrev}
              previousSlideText={previousSlideText}
              isPrevDisabled={prevBtnDisabled}
              onNextClick={scrollNext}
              isNextDisabled={nextBtnDisabled}
              nextSlideText={nextSlideText}
            >
              {children}
            </NavBarLegacy>
          </>
        )}
      </BoxWithCtaAnalytics>
    </RestrictContent>
  );
};

Carousel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  entryTitle: PropTypes.string.isRequired,
  viewDuration: PropTypes.number.isRequired,
  areAllSlidesSameHeight: PropTypes.bool,
  previousSlideText: PropTypes.string,
  nextSlideText: PropTypes.string,
  selectSlideText: PropTypes.string,
  playText: PropTypes.string,
  pauseText: PropTypes.string,
  promoBannerNoBottomSpace: PropTypes.bool,
  showProgressBar: PropTypes.bool.isRequired,
  slideFixedHeight: PropTypes.number,
  slideMobileFixedHeight: PropTypes.number,
  slideSpacing: PropTypes.number,
  restrictCarouselContent: PropTypes.bool,
  maxWidth: PropTypes.number,
};

export default Carousel;
